import {
  DestroyRef,
  Injectable,
  computed,
  effect,
  inject,
  signal
} from '@angular/core';
import type { Unsubscribe } from '@angular/fire/firestore';
import {
  Firestore,
  collection,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  where
} from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';

import { AuthStore } from '@core/services/auth/auth.store';
import { LoggerService } from '@core/services/infra/logger.service';
import { isPermissionDeniedError } from '@core/services/auth/auth-transition-error';
import {
  SUPPORT_LIMITS,
  toDate,
  type SupportAttachment,
  type SupportAudience,
  type SupportContext,
  type SupportLang,
  type SupportMessage,
  type SupportThread
} from '@shared/definitions/support';

/**
 * The support conversation, client side (#284).
 *
 * Shaped after `AiAssistantService`, deliberately: the two live side by side as
 * tabs in the same docked panel, and a user switching between them should not
 * meet two different notions of "sending". What is copied is the mechanism —
 * signal state, a Firestore listener for the stream, a callable for the write.
 * What is NOT copied is the optimistic-echo machinery: a support message is
 * answered by a human in minutes, not streamed token by token, so a pending
 * spinner is honest where a fake bubble would not be.
 *
 * READS ARE LISTENERS, WRITES ARE CALLABLES. `firestore.rules` denies every
 * client write on `supportThreads`, so there is no path here that could work
 * around a status transition or forge the Slack coordinates.
 */
@Injectable({ providedIn: 'root' })
export class SupportStore {
  private readonly firestore = inject(Firestore);
  private readonly functions = inject(Functions);
  private readonly authStore = inject(AuthStore);
  private readonly destroyRef = inject(DestroyRef);
  private readonly logger = inject(LoggerService);

  private readonly _threads = signal<readonly SupportThread[]>([]);
  private readonly _activeThreadId = signal<string | null>(null);
  private readonly _messages = signal<readonly SupportMessage[]>([]);
  private readonly _sending = signal(false);
  private readonly _loadingMessages = signal(false);
  private readonly _lastError = signal<string | null>(null);
  /**
   * Bumped by `requestOpen()`. The panel component watches it rather than the
   * store owning panel state: the panel is shared with the assistant, and two
   * services both claiming to own "is it open" is how that gets out of sync.
   */
  private readonly _openRequest = signal(0);

  /**
   * A ticket the assistant handoff has prepared but not sent (#291).
   *
   * Deliberately NOT sent on the user's behalf: they clicked "talk to a human",
   * which is a request to write to us, not a message. The compose form opens
   * pre-filled so they can say what the assistant got wrong — and can still
   * change their mind, which an auto-sent ticket would take away.
   */
  private readonly _handoffDraft = signal<{
    transcript: string;
    conversationId?: string;
    subject: string;
  } | null>(null);

  readonly threads = this._threads.asReadonly();
  readonly activeThreadId = this._activeThreadId.asReadonly();
  readonly messages = this._messages.asReadonly();
  readonly sending = this._sending.asReadonly();
  readonly loadingMessages = this._loadingMessages.asReadonly();
  readonly lastError = this._lastError.asReadonly();
  readonly openRequest = this._openRequest.asReadonly();
  readonly handoffDraft = this._handoffDraft.asReadonly();

  readonly activeThread = computed(
    () => this._threads().find(t => t.id === this._activeThreadId()) ?? null
  );

  /** Drives the badge on the Support tab. */
  readonly unreadCount = computed(() => this._threads().filter(t => t.unreadForUser).length);

  readonly hasThreads = computed(() => this._threads().length > 0);

  private threadsUnsub: Unsubscribe | null = null;
  private messagesUnsub: Unsubscribe | null = null;
  /** Guards against re-claiming on every token refresh. */
  private claimedForUid: string | null = null;

  constructor() {
    effect(() => {
      const uid = this.authStore.user()?.userUID ?? null;
      this.subscribeToThreads(uid);
      if (uid) void this.claimOrphanThreads(uid);
    });

    effect(() => {
      this.subscribeToMessages(this._activeThreadId());
    });

    this.destroyRef.onDestroy(() => {
      this.threadsUnsub?.();
      this.messagesUnsub?.();
    });
  }

  // -------------------------------------------------------------------------
  // Panel plumbing
  // -------------------------------------------------------------------------

  /**
   * Ask the docked panel to open on the Support tab.
   *
   * @param threadId Optional thread to select — used by the notification
   *   deep-link `/?support=<id>`.
   */
  requestOpen(threadId?: string): void {
    if (threadId) this._activeThreadId.set(threadId);
    this._openRequest.update(n => n + 1);
  }

  /**
   * Stage a ticket from the assistant handoff, and ask the panel to show it.
   *
   * @param draft The transcript, the conversation it came from, and a subject
   *   derived from the user's first line.
   */
  startHandoff(draft: { transcript: string; conversationId?: string; subject: string }): void {
    this._handoffDraft.set(draft);
    this._activeThreadId.set(null);
    this._openRequest.update(n => n + 1);
  }

  /** Drop the staged handoff — sent, or abandoned. */
  clearHandoff(): void {
    this._handoffDraft.set(null);
  }

  selectThread(threadId: string | null): void {
    this._activeThreadId.set(threadId);
    if (threadId) void this.markRead(threadId);
  }

  // -------------------------------------------------------------------------
  // Writes
  // -------------------------------------------------------------------------

  /**
   * Open a ticket.
   *
   * Works signed out — that is the point of the public form — in which case the
   * reply arrives by e-mail and `visibleInApp` comes back false so the UI can
   * say so instead of pointing at a fil the user will never see.
   *
   * @param input Subject, body and the optional consented context.
   * @returns The new thread id, or `null` when the call failed.
   */
  async createThread(input: {
    subject: string;
    message: string;
    email?: string;
    displayName?: string;
    locale?: SupportLang;
    context?: SupportContext | null;
    attachments?: SupportAttachment[];
    fromPublicForm?: boolean;
    /** Assistant transcript carried over by the handoff (#291). */
    systemNote?: string;
    /** `workspace` addresses the tenant's own manager (#292). */
    audience?: SupportAudience;
    workspaceId?: string;
  }): Promise<{ threadId: string; visibleInApp: boolean } | null> {
    this._sending.set(true);
    this._lastError.set(null);
    try {
      const callable = httpsCallable<
        Record<string, unknown>,
        { threadId: string; visibleInApp: boolean }
      >(this.functions, 'supportCreateThread');
      const result = await callable(this.stripUndefined({
        subject: input.subject,
        message: input.message,
        email: input.email,
        displayName: input.displayName,
        locale: input.locale,
        // `null` is meaningful here — it is what "the user unticked the
        // consent box" looks like — so it is passed through, not stripped.
        context: input.context ?? null,
        attachments: input.attachments,
        fromPublicForm: input.fromPublicForm,
        systemNote: input.systemNote,
        audience: input.audience,
        workspaceId: input.workspaceId
      }));
      if (result.data?.visibleInApp) {
        this._activeThreadId.set(result.data.threadId);
      }
      return result.data;
    } catch (err) {
      this.captureError('supportCreateThread', err);
      return null;
    } finally {
      this._sending.set(false);
    }
  }

  /**
   * Reply inside an existing ticket.
   *
   * @param threadId The thread.
   * @param message The reply body.
   * @returns True when the write landed.
   */
  async reply(threadId: string, message: string): Promise<boolean> {
    const trimmed = message.trim();
    if (!trimmed) return false;
    if (trimmed.length > SUPPORT_LIMITS.messageMaxLength) {
      this._lastError.set('support.errors.tooLong');
      return false;
    }

    this._sending.set(true);
    this._lastError.set(null);
    try {
      const callable = httpsCallable<{ threadId: string; message: string }, { ok: true }>(
        this.functions,
        'supportReply'
      );
      await callable({ threadId, message: trimmed });
      return true;
    } catch (err) {
      this.captureError('supportReply', err);
      return false;
    } finally {
      this._sending.set(false);
    }
  }

  /**
   * Clear the unread flag on a thread the user is looking at.
   *
   * Best-effort: a failure here means a badge stays lit, which is worth
   * exactly zero user-facing error copy.
   *
   * @param threadId The thread being read.
   */
  async markRead(threadId: string): Promise<void> {
    const thread = this._threads().find(t => t.id === threadId);
    if (!thread?.unreadForUser) return;
    try {
      const callable = httpsCallable<{ threadId: string }, { ok: true }>(
        this.functions,
        'supportMarkRead'
      );
      await callable({ threadId });
    } catch (err) {
      this.logger.warn('supportMarkRead failed', 'SupportStore', { err: String(err) });
    }
  }

  /**
   * Close a ticket. Replying to it later reopens it — see `supportReply`.
   *
   * @param threadId The thread to close.
   */
  async closeThread(threadId: string): Promise<void> {
    try {
      const callable = httpsCallable<{ threadId: string }, { ok: true }>(
        this.functions,
        'supportCloseThread'
      );
      await callable({ threadId });
    } catch (err) {
      this.captureError('supportCloseThread', err);
    }
  }

  /**
   * Translate one message into a UI language, cached server-side.
   *
   * @param threadId The thread.
   * @param messageId The message.
   * @param targetLang Target language.
   * @returns The translated text, or null on failure.
   */
  async translate(
    threadId: string,
    messageId: string,
    targetLang: SupportLang
  ): Promise<string | null> {
    try {
      const callable = httpsCallable<
        { threadId: string; messageId: string; targetLang: SupportLang },
        { ok: boolean; text: string; fromCache: boolean }
      >(this.functions, 'supportTranslateMessage');
      const result = await callable({ threadId, messageId, targetLang });
      return result.data?.text ?? null;
    } catch (err) {
      this.captureError('supportTranslateMessage', err);
      return null;
    }
  }

  // -------------------------------------------------------------------------
  // Listeners
  // -------------------------------------------------------------------------

  private subscribeToThreads(uid: string | null): void {
    this.threadsUnsub?.();
    this.threadsUnsub = null;
    if (!uid) {
      this._threads.set([]);
      this._activeThreadId.set(null);
      this.claimedForUid = null;
      return;
    }

    // #441 lot 4: bounded — the badge only ever needs the recent window, not
    // every ticket a user has ever opened.
    const q = query(
      collection(this.firestore, 'supportThreads'),
      where('userId', '==', uid),
      orderBy('lastMessageAt', 'desc'),
      limit(20)
    );
    this.threadsUnsub = onSnapshot(
      q,
      snapshot => {
        this._threads.set(
          snapshot.docs.map(d => {
            const data = d.data();
            return {
              id: d.id,
              audience: data['audience'],
              status: data['status'],
              subject: data['subject'] ?? '',
              email: data['email'] ?? '',
              displayName: data['displayName'] ?? '',
              locale: data['locale'] ?? 'en',
              userId: data['userId'] ?? null,
              workspaceId: data['workspaceId'] ?? null,
              context: data['context'] ?? null,
              unreadForUser: data['unreadForUser'] === true,
              messageCount: data['messageCount'] ?? 0,
              createdAt: toDate(data['createdAt']),
              updatedAt: toDate(data['updatedAt']),
              lastMessageAt: toDate(data['lastMessageAt']),
              lastStaffReplyAt: toDate(data['lastStaffReplyAt'])
            } as SupportThread;
          })
        );
      },
      err => this.onListenerError('threads', err)
    );
  }

  private subscribeToMessages(threadId: string | null): void {
    this.messagesUnsub?.();
    this.messagesUnsub = null;
    this._messages.set([]);
    if (!threadId) return;

    this._loadingMessages.set(true);
    const q = query(
      collection(doc(this.firestore, 'supportThreads', threadId), 'messages'),
      orderBy('createdAt', 'asc')
    );
    this.messagesUnsub = onSnapshot(
      q,
      snapshot => {
        this._loadingMessages.set(false);
        this._messages.set(
          snapshot.docs.map(d => {
            const data = d.data();
            return {
              id: d.id,
              role: data['role'] ?? 'user',
              authorId: data['authorId'] ?? null,
              authorName: data['authorName'] ?? '',
              content: data['content'] ?? '',
              source: data['source'] ?? 'app',
              attachments: data['attachments'] ?? null,
              bodyTranslations: data['bodyTranslations'] ?? null,
              createdAt: toDate(data['createdAt'])
            } as SupportMessage;
          })
        );
      },
      err => {
        this._loadingMessages.set(false);
        this.onListenerError('messages', err);
      }
    );
  }

  /**
   * Attach tickets opened before signing in.
   *
   * The server refuses unless the account's e-mail is VERIFIED — see
   * `supportClaimThreads`. Calling it here on every sign-in is cheap (one
   * indexed query returning nothing in the overwhelming majority of cases) and
   * is the only moment we know the user just proved who they are.
   */
  private async claimOrphanThreads(uid: string): Promise<void> {
    if (this.claimedForUid === uid) return;
    this.claimedForUid = uid;
    try {
      const callable = httpsCallable<Record<string, never>, { claimed: number }>(
        this.functions,
        'supportClaimThreads'
      );
      const result = await callable({});
      if (result.data?.claimed) {
        this.logger.info('Support: orphan tickets attached', 'SupportStore', {
          claimed: result.data.claimed
        });
      }
    } catch (err) {
      // Never surfaced: the user did not ask for this, and a failure only means
      // an old ticket stays e-mail-only.
      this.logger.warn('supportClaimThreads failed', 'SupportStore', { err: String(err) });
    }
  }

  // -------------------------------------------------------------------------
  // Plumbing
  // -------------------------------------------------------------------------

  private onListenerError(which: string, err: unknown): void {
    // A permission-denied here is the normal shape of a sign-out: the listener
    // fires once more on the previous account before unsubscribe propagates.
    if (isPermissionDeniedError(err)) return;
    this.logger.error(`Support ${which} listener failed`, 'SupportStore', { err: String(err) });
  }

  private captureError(operation: string, err: unknown): void {
    const message = err instanceof Error ? err.message : String(err);
    this.logger.error(`${operation} failed`, 'SupportStore', { err: message });
    this._lastError.set(message);
  }

  /**
   * Drop `undefined` keys before they reach a callable.
   *
   * Not the Firestore rule (these go through a callable, not `setDoc`), but the
   * same discipline: an explicit `undefined` in the payload serialises to a key
   * the backend then has to defend against.
   */
  private stripUndefined(input: Record<string, unknown>): Record<string, unknown> {
    return Object.fromEntries(Object.entries(input).filter(([, v]) => v !== undefined));
  }
}
